import {
  FLOW_METRICS_INTERVAL_LATEST_RUN,
  FLOW_METRICS_INTERVAL_LIFETIME,
  isWindowInterval,
  resolveEffectiveInterval,
} from 'js/constants/metricsConstants';
import { resolveWindowRange } from 'new/state/metrics/metricsWindow';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { fetchFlowRunLogs, loadFlowMetrics, runSource, loadFlowMetricsByRunId } from 'js/actions';
import { loadFileDetails } from 'js/actions/metrics/fileDetails';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { loadFlowRuns } from 'js/actions/metrics/flowRuns';
import {
  fetchFlowRunLogsWithFacets,
  Options as FetchFlowRunLogsWithFacetsOptions,
} from 'js/actions/metrics/flowRunLogsWithFacets';
import {
  fetchFlowRunSummary,
  clearFlowRunSummary as clearFlowRunSummaryAction,
} from 'js/actions/metrics';
import useSources from 'new/state/sources/repository/useSources';
import usePaging from 'new/state/paging/repository/usePaging';

type FetchFlowRunLogsOptions = {
  runId: number;
  from: number;
};

interface MetricsAdapterHookReturn {
  flowMetricsInterval: string;
  requestFlowRuns: (params?: { page?: number }) => Promise<any>;
  /**
   * @HowToUse
   * - `latestRun` (the default) needs `sourceId` to look up `last_run_id`. Omit it and this
   *   silently does nothing.
   *
   * - Lifetime works without `sourceId`: pass `{ interval: FLOW_METRICS_INTERVAL_LIFETIME }`.
   * - Windowed intervals (`last24h`, `today`, ...) need `sourceId` too, to check the interval
   *   is valid for that source's category.
   *
   * - Don't dispatch `loadFlowMetrics` directly at the call site. Even for a fixed interval,
   *   go through this hook so every metrics fetch stays behind the adapter.
   */
  requestFlowMetrics: (
    flowNodeId: string | number,
    sourceId?: string | number,
    options?: { interval: string },
  ) => void;
  requestFlowMetricsByRunId: (flowNodeId: string | number, runId: number) => Promise<void>;
  requestFileDetails: (
    resourceId: number,
    fileName: string,
    resourceType: string,
  ) => Promise<{
    data: Array<{
      dataSetId: number;
      size: number;
      ingestionStatus: string;
      recordCount: number;
      runId: number;
      lastIngested: string;
      errorCount: number;
    }>;
    meta: {
      totalCount: number;
      pageCount: number;
      currentPage: number;
    };
  }>;
  requestFlowRunLogsWithFacets: (
    originNodeId: string | number,
    options?: { params?: any; body?: any },
  ) => Promise<void>;
  requestFlowRunLogs: (
    originNodeId: string | number,
    options?: FetchFlowRunLogsOptions,
  ) => Promise<void>;
  requestFlowRunSummary: (originNodeId: string, options?: any) => Promise<any>;
  clearFlowRunSummary: () => Promise<void>;
  runFlowNow: (dataSourceId: string) => Promise<any>;
}

export type UseMetricsAdapterReturnedType<T extends keyof MetricsAdapterHookReturn> = Awaited<
  ReturnType<MetricsAdapterHookReturn[T]>
>;

type RequestFlowMetrics = MetricsAdapterHookReturn['requestFlowMetrics'];
type RequestFileDetails = MetricsAdapterHookReturn['requestFileDetails'];
type RequestFlowRuns = MetricsAdapterHookReturn['requestFlowRuns'];
type RequestFlowMetricsByRunId = MetricsAdapterHookReturn['requestFlowMetricsByRunId'];

export default function useMetricsAdapter(): MetricsAdapterHookReturn {
  const dispatch = useAppDispatch();
  const flowMetricsInterval = useSelector(getFlowMetricsInterval);
  const { getSourceById } = useSources();
  const { getConnectionCategoryByConnectorType } = usePaging();

  const requestFlowMetricsByRunId: RequestFlowMetricsByRunId = useCallback(
    async (flowNodeId, runId) => {
      dispatch(loadFlowMetricsByRunId(flowNodeId, runId));
    },
    [dispatch],
  );

  const requestFlowMetrics: RequestFlowMetrics = useCallback(
    (flowNodeId, sourceId = null, options = { interval: FLOW_METRICS_INTERVAL_LATEST_RUN }) => {
      if (!flowNodeId) return;

      const source = sourceId ? getSourceById(Number(sourceId)) : null;
      const connectionCategory = source?.connection_type
        ? getConnectionCategoryByConnectorType(source.connection_type)
        : undefined;
      const sourceContext = { sourceType: source?.source_type, connectionCategory };
      const effectiveInterval = resolveEffectiveInterval(sourceContext, options.interval);

      if (isWindowInterval(effectiveInterval)) {
        const range = resolveWindowRange(effectiveInterval);
        if (range) {
          dispatch(loadFlowMetrics(flowNodeId, { from: range.from, to: range.to }));
        }
      } else if (effectiveInterval === FLOW_METRICS_INTERVAL_LIFETIME) {
        dispatch(loadFlowMetrics(flowNodeId, { ...options, interval: effectiveInterval }));
      } else if (effectiveInterval === FLOW_METRICS_INTERVAL_LATEST_RUN) {
        if (!sourceId) return;

        const latestRunId = source?.last_run_id ?? null;
        if (latestRunId != null) {
          requestFlowMetricsByRunId(flowNodeId, latestRunId);
        }
      }
    },
    [dispatch, getSourceById, getConnectionCategoryByConnectorType, requestFlowMetricsByRunId],
  );

  const requestFileDetails: RequestFileDetails = useCallback(
    async (resourceId, fileName, resourceType) => {
      const args = { fileName, resourceId, resourceType };
      const resp = await tryCatchAsync(dispatch(loadFileDetails(args)));

      if (resp?.error) {
        console.error(`requestFileDetails:`, resp?.error);
        return null;
      }

      return resp.data?.result?.output;
    },
    [dispatch],
  );

  const requestFlowRuns: RequestFlowRuns = useCallback(
    async (params?: { page?: number }) => {
      const resp = await tryCatchAsync(dispatch(loadFlowRuns(params)));

      if (resp?.error) {
        console.error(`requestFlowRuns:`, resp?.error);
        return null;
      }

      return resp.data?.result?.output;
    },
    [dispatch],
  );

  const requestFlowRunLogs = useCallback(
    async (originNodeId, options?: FetchFlowRunLogsOptions) => {
      dispatch(fetchFlowRunLogs(originNodeId as string, options));
    },
    [dispatch],
  );

  const requestFlowRunLogsWithFacets = useCallback(
    async (originNodeId, options?: FetchFlowRunLogsWithFacetsOptions) => {
      dispatch(fetchFlowRunLogsWithFacets(originNodeId as string, options));
    },
    [dispatch],
  );

  const requestFlowRunSummary = useCallback(
    async (originNodeId: string, options?: any) => {
      return dispatch(fetchFlowRunSummary(originNodeId, options));
    },
    [dispatch],
  );

  const clearFlowRunSummary = useCallback(async () => {
    return dispatch(clearFlowRunSummaryAction()) as unknown as Promise<void>;
  }, [dispatch]);

  const runFlowNow = useCallback(
    async (dataSourceId: string) => {
      return dispatch(runSource(dataSourceId));
    },
    [dispatch],
  );

  return {
    flowMetricsInterval,
    requestFlowMetrics,
    requestFileDetails,
    requestFlowRuns,
    requestFlowMetricsByRunId,
    requestFlowRunLogsWithFacets,
    requestFlowRunLogs,
    requestFlowRunSummary,
    clearFlowRunSummary,
    runFlowNow,
  };
}

export { useMetricsAdapter };
